import React, { FC, useEffect, useRef } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { ButtonComponent } from '../ButtonComponent';
import { FilterComponent } from '../FilterComponent';
import { SelectPicker } from 'rsuite';
import { useTranslation } from 'react-i18next';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useDebounce } from 'use-debounce';
import { useIdentity } from '../../store/identyti/identity-context';
import { fetchUsersList } from '../../store/identyti/actions/users';

const FILTER_TYPE = [
	{
		label: 'First name',
		value: 'given_name'
	},
	{
		label: 'Last name',
		value: 'family_name'
	},
	{
		label: 'Email',
		value: 'email'
	}
	// {
	// 	label: 'User name',
	// 	value: 'username'
	// }
];

export interface IUserFilters {
	filterBy?: string;
	query?: string;
	pageNumber?: number;
}

interface Props {
	setIsAddUserModalOpen: (value: boolean) => void;
	filterBy: string;
	setFilterBy: (value: string) => void;
	query: string;
	setQuery: (value: string) => void;
	currentPage: number;
}

export const UserFilterSection: FC<Props> = (props) => {
	const {
		setIsAddUserModalOpen,
		filterBy, setFilterBy,
		query, setQuery, currentPage,
	} = props;

	const { t } = useTranslation();
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const isMounted = useRef(false);

	const {
		identityState: { isUserListLoading, usersListPaginationTokens },
		dispatch
	} = useIdentity();

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const filtersFromUrl: IUserFilters = parseSearchParams(search) || {};

	const [value] = useDebounce(query.trim(), 1000);

	useEffect(() => {
		if (!isMounted.current) return;

		const params: IUserFilters = filtersFromUrl;
		params.filterBy = query ? filterBy : '';
		params.query = query;

		navigate(`${pathname}?${makeSearchParams(params)}`);
	}, [filterBy, value]);

	useEffect(() => {
		if (!isMounted.current) return;
		void fetchUsersList({
			dispatch,
			usersListPaginationTokens,
			currentPage,
			searchParams: filtersFromUrl
		});
	}, [currentPage]);

	useEffect(() => {
		if (!isMounted.current) {
			// first mount
			isMounted.current = true;
		}
	}, []);

	return (
		<div className="card bg-transparent">
			<div className="card-body px-0 py-0">
				<div className="d-sm-flex align-items-center justify-content-between mb-4">
					<div className="application-selector">
						<SearchInputSmall
							placeholder={t('USER.SEARCH_USERS')}
							searchValue={query}
							setSearchValue={setQuery}
							isDisabled={isUserListLoading}
						/>
					</div>

					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN) && (
							<div>
								<ButtonComponent
									buttonName="Add User"
									onButtonClick={(): void => setIsAddUserModalOpen(true)}
									additionalClass="w-100 mt-5 mt-sm-0 d-none d-sm-block btn-sm"
								/>
							</div>
						)
					}
				</div>
				<div className="d-flex align-items-center fc-filter-line">
					<FilterComponent value={filterBy} label="Filter by" labelPlacement="integrated">
						<SelectPicker
							data={FILTER_TYPE}
							appearance="subtle"
							placement="bottomEnd"
							placeholder="Filter by"
							className="fc-selector timezone-selector"
							onSelect={(value): void => setFilterBy(value)}
							value={filterBy}
							searchable={false}
							cleanable={false}
							disabled={isUserListLoading}
						/>
					</FilterComponent>
				</div>
				{
					checkRoles(authUser, CLAIMS.MERCHANT_ADMIN) && (
						<ButtonComponent
							buttonName="Add User"
							onButtonClick={(): void => setIsAddUserModalOpen(true)}
							additionalClass="w-100 my-4 d-block d-sm-none"
						/>
					)
				}
			</div>
		</div>
	);
};
